import React, { useState, useRef } from 'react';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { TokenManager } from 'utils/tokenManager';

interface UploadResponse {
  uuid: string;
  key: string;
  url: string;
}

interface UploadedFile {
  filename: string;
  key: string;
  size: number;
  type: string;
  extension: string;
}

interface S3FileUploaderProps {
  uploadEndpoint: string;
  multiple?: boolean;
  onUploadComplete: (uploadedFiles: UploadedFile[]) => void;
  selectButton?: React.ReactNode; // Allows custom button UI
}

const S3FileUploader: React.FC<S3FileUploaderProps> = ({
  uploadEndpoint,
  multiple = false,
  onUploadComplete,
  selectButton,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function getUploadUrl(filename: string, contentType: string) {
    let token = await TokenManager.getToken();

    const fetchUploadUrl = async () => {
      const res = await fetch(uploadEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ filename, content_type: contentType }),
      });

      if (res.status === 401) {
        token = await TokenManager.refreshToken();
        return fetchUploadUrl();
      }

      if (!res.ok) throw new Error('Failed to get upload URL');

      return res.json();
    };

    return fetchUploadUrl();
  }

  const uploadToS3 = (
    file: File,
    uploadUrl: UploadResponse,
  ): Promise<UploadResponse> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl.url, true);
      xhr.setRequestHeader('Content-Type', file.type);

      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setProgress(prev => ({ ...prev, [file.name]: percent }));
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) resolve(uploadUrl);
        else reject(new Error(`Upload failed with status ${xhr.status}`));
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));

      xhr.send(file);
    });
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let selectedFiles = Array.from(event.target.files || []);

    if (!multiple) {
      selectedFiles = selectedFiles.slice(0, 1);
    }

    setFiles(selectedFiles);

    const uploadPromises = selectedFiles.map(async file => {
      try {
        const uploadUrl = await getUploadUrl(file.name, file.type);
        await uploadToS3(file, uploadUrl);

        return {
          filename: file.name,
          key: uploadUrl.key,
          size: file.size,
          type: file.type,
          extension: file.name.split('.').pop() || '',
        };
      } catch (error) {
        console.error('Upload failed:', error);
        return null;
      }
    });

    const uploadedFiles = (await Promise.all(uploadPromises)).filter(
      (file): file is UploadedFile => file !== null,
    );

    if (uploadedFiles.length > 0) {
      onUploadComplete(uploadedFiles);
    }

    setFiles([]); // Reset state after upload
    setProgress({});
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    if (fileInputRef.current) {
      fileInputRef.current.files = event.dataTransfer.files;
      handleFileChange({ target: fileInputRef.current } as any);
    }
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Box
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        border: '2px dashed gray',
        borderRadius: '8px',
        padding: '16px',
        textAlign: 'center',
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' },
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleFileChange}
        {...(multiple ? { multiple: true } : {})}
      />

      {/* If a custom button is provided, use it; otherwise, default to "Select Files" */}
      <Box onClick={handleButtonClick}>
        {selectButton || (
          <Button sx={{ display: 'block', width: '100%' }}>
            Select File{multiple ? 's' : ''}
          </Button>
        )}
      </Box>

      {files.length > 0 && (
        <Box mt={2}>
          {files.map(file => (
            <Box key={file.name} mb={1}>
              <Typography variant="body2">{file.name}</Typography>
              <LinearProgress
                variant="determinate"
                value={progress[file.name] || 0}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default S3FileUploader;
